<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

// Etapas de UM funil: criar, editar, reordenar e remover. As quatro ações
// chegam como função (mesmo padrão de KanbanFilterBar.vue) porque a remoção é
// uma conversa de DOIS passos com o servidor — tentar sem destino, e só então
// perguntar para onde vão os cards — e essa conversa precisa da resposta da
// chamada, não de um evento de ida.
//
// Reordenar é por BOTÃO, não por arrasto: é a única forma que funciona por
// teclado sem reimplementar drag acessível, e a lista de etapas é curta.
const props = defineProps({
  funnel: { type: Object, required: true },
  isSubmitting: { type: Boolean, default: false },
  createStage: { type: Function, required: true },
  updateStage: { type: Function, required: true },
  deleteStage: { type: Function, required: true },
  reorderStages: { type: Function, required: true },
});

const { t } = useI18n();

const stages = computed(() => props.funnel.stages ?? []);

const ROLE_OPTIONS = ['administrator', 'agent'];

const listRef = ref(null);
const nameInputRef = ref(null);

// null = nenhum formulário aberto; 'new' = criando; número = editando a etapa.
const formTarget = ref(null);
const form = ref({ name: '', wipLimit: '', slaHours: '', requiredRole: '' });
const errorKey = ref(null);
const errorCardsCount = ref(null);

// Remoção pendente de destino: guarda a etapa a remover e quantas conversas
// ela tem (o servidor manda a contagem no 409 — é ela que a pergunta usa).
const removal = ref(null);

const isEditing = computed(() => typeof formTarget.value === 'number');
const fieldId = suffix =>
  `kanban-stage-${props.funnel.id}-${formTarget.value ?? 'none'}-${suffix}`;

const clearError = () => {
  errorKey.value = null;
  errorCardsCount.value = null;
};

const applyResult = result => {
  if (result.ok) {
    clearError();
    return true;
  }
  errorKey.value = result.errorKey;
  errorCardsCount.value = result.cardsCount;
  return false;
};

const focusNameInput = async () => {
  await nextTick();
  nameInputRef.value?.$el?.querySelector('input')?.focus();
};

const openCreate = () => {
  clearError();
  removal.value = null;
  formTarget.value = 'new';
  form.value = { name: '', wipLimit: '', slaHours: '', requiredRole: '' };
  focusNameInput();
};

const openEdit = stage => {
  clearError();
  removal.value = null;
  formTarget.value = stage.id;
  form.value = {
    name: stage.name,
    wipLimit: stage.wip_limit ?? '',
    slaHours: stage.sla_hours ?? '',
    requiredRole: stage.required_role ?? '',
  };
  focusNameInput();
};

const closeForm = () => {
  formTarget.value = null;
};

// Campo vazio vira null EXPLÍCITO, e não ausente: no controller, ausente não
// altera e presente-com-null limpa — e limpar é o que "apagar o limite" quer
// dizer. Os três campos nulos têm significado próprio ("sem limite", "sem
// meta", "qualquer papel").
const numberOrNull = value => {
  const raw = String(value ?? '').trim();
  return raw === '' ? null : Number(raw);
};

const formAttributes = () => ({
  name: form.value.name.trim(),
  wip_limit: numberOrNull(form.value.wipLimit),
  sla_hours: numberOrNull(form.value.slaHours),
  required_role: form.value.requiredRole || null,
});

const canSubmitForm = computed(
  () => form.value.name.trim().length > 0 && !props.isSubmitting
);

const submitForm = async () => {
  if (!canSubmitForm.value) return;

  const attributes = formAttributes();
  const result = isEditing.value
    ? await props.updateStage(props.funnel.id, formTarget.value, attributes)
    : await props.createStage(props.funnel.id, attributes);

  if (applyResult(result)) closeForm();
};

// Passo 1 da remoção: DELETE sem destino. Etapa vazia some aqui mesmo; etapa
// com card volta 409 e o destino é perguntado no passo 2.
const attemptRemove = async stage => {
  clearError();
  closeForm();
  const result = await props.deleteStage(props.funnel.id, stage.id);
  if (result.ok) {
    removal.value = null;
    return;
  }
  if (result.errorKey === 'STAGE_NEEDS_TARGET') {
    const [firstOther] = stages.value.filter(item => item.id !== stage.id);
    removal.value = {
      stageId: stage.id,
      stageName: stage.name,
      cardsCount: result.cardsCount,
      targetStageId: firstOther?.id ?? null,
    };
    return;
  }
  applyResult(result);
};

const removalTargets = computed(() =>
  stages.value.filter(stage => stage.id !== removal.value?.stageId)
);

const cancelRemoval = () => {
  removal.value = null;
};

const confirmRemoval = async () => {
  const pending = removal.value;
  if (!pending?.targetStageId) return;

  const result = await props.deleteStage(props.funnel.id, pending.stageId, {
    targetStageId: pending.targetStageId,
  });
  if (applyResult(result)) removal.value = null;
};

// Depois de reordenar, o foco volta para o controle equivalente da MESMA
// etapa. Quando ela chega à ponta, o botão usado fica desabilitado (e um
// elemento desabilitado devolve o foco a BODY), então o foco vai para o botão
// da direção oposta — quem navega por teclado continua na linha que moveu.
const focusReorderControl = async (stageId, direction) => {
  await nextTick();
  const row = listRef.value?.querySelector(`[data-stage-row="${stageId}"]`);
  const preferred = row?.querySelector(`[data-reorder="${direction}"]`);
  const opposite = row?.querySelector(
    `[data-reorder="${direction === 'up' ? 'down' : 'up'}"]`
  );
  const target = preferred && !preferred.disabled ? preferred : opposite;
  target?.focus();
};

// A API recebe a lista COMPLETA na ordem desejada: reordenar é uma operação
// sobre o conjunto, e lista parcial é 422.
const move = async (index, offset) => {
  const ids = stages.value.map(stage => stage.id);
  const target = index + offset;
  if (target < 0 || target >= ids.length) return;

  const movedId = ids[index];
  ids.splice(target, 0, ids.splice(index, 1)[0]);
  const result = await props.reorderStages(props.funnel.id, ids);
  applyResult(result);
  focusReorderControl(movedId, offset < 0 ? 'up' : 'down');
};

const roleLabel = role =>
  role === 'administrator'
    ? t('KANBAN.ADMIN.STAGES.ROLE_ADMINISTRATOR')
    : t('KANBAN.ADMIN.STAGES.ROLE_AGENT');

// Os três valores eram <span> irmãos, sem separador nenhum: com limite e meta
// preenchidos a linha saía "Limite: 50Meta: 24h", e com os três
// "Limite: 50Meta: 24hPapel: Administrador". Juntar aqui (mesmo padrão de
// reasonLabel em KanbanDebthubLinkPanel.vue) também elimina o <p> vazio da
// etapa sem nenhum dos três.
const stageMeta = stage =>
  [
    stage.wip_limit
      ? t('KANBAN.ADMIN.STAGES.WIP_LIMIT_VALUE', { count: stage.wip_limit })
      : null,
    stage.sla_hours
      ? t('KANBAN.ADMIN.STAGES.SLA_VALUE', { hours: stage.sla_hours })
      : null,
    stage.required_role
      ? t('KANBAN.ADMIN.STAGES.ROLE_VALUE', {
          role: roleLabel(stage.required_role),
        })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between gap-2">
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ t('KANBAN.ADMIN.STAGES.SECTION_TITLE') }}
      </h3>
      <NextButton
        v-if="stages.length"
        slate
        faded
        xs
        icon="i-lucide-plus"
        :disabled="isSubmitting"
        :label="t('KANBAN.ADMIN.STAGES.ADD')"
        @click="openCreate"
      />
    </div>

    <!-- Funil sem etapa é INERTE (o board devolve zero coluna). O estado
         vazio não é decorativo: ele é o que conduz à primeira etapa. -->
    <div
      v-if="!stages.length"
      class="flex flex-col items-start gap-1 p-3 rounded-lg bg-n-alpha-1"
    >
      <p class="m-0 text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.ADMIN.STAGES.EMPTY_TITLE') }}
      </p>
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('KANBAN.ADMIN.STAGES.EMPTY_DESCRIPTION') }}
      </p>
      <!-- !text-n-blue-12: ver a justificativa medida em
           KanbanDebthubLinkPanel.vue (a variante `blue faded` compartilhada
           reprova 1.4.3 no tema claro; o override não toca o token). -->
      <NextButton
        blue
        faded
        xs
        class="mt-1 !text-n-blue-12"
        data-testid="kanban-stage-empty-action"
        :disabled="isSubmitting"
        :label="t('KANBAN.ADMIN.STAGES.ADD_FIRST')"
        @click="openCreate"
      />
    </div>

    <ul v-else ref="listRef" class="flex flex-col gap-1 m-0 p-0 list-none">
      <li
        v-for="(stage, index) in stages"
        :key="stage.id"
        :data-stage-row="stage.id"
        class="flex flex-wrap items-center justify-between gap-2 p-2 rounded-lg bg-n-alpha-1"
      >
        <div class="min-w-0">
          <p class="m-0 text-sm truncate text-n-slate-12">{{ stage.name }}</p>
          <p
            v-if="stageMeta(stage)"
            data-testid="kanban-stage-meta"
            class="m-0 text-xs text-n-slate-11"
          >
            {{ stageMeta(stage) }}
          </p>
        </div>

        <div class="flex items-center gap-1 shrink-0">
          <NextButton
            slate
            ghost
            xs
            icon="i-lucide-arrow-up"
            data-reorder="up"
            :disabled="index === 0 || isSubmitting"
            :aria-label="t('KANBAN.ADMIN.STAGES.MOVE_UP', { name: stage.name })"
            @click="move(index, -1)"
          />
          <NextButton
            slate
            ghost
            xs
            icon="i-lucide-arrow-down"
            data-reorder="down"
            :disabled="index === stages.length - 1 || isSubmitting"
            :aria-label="
              t('KANBAN.ADMIN.STAGES.MOVE_DOWN', { name: stage.name })
            "
            @click="move(index, 1)"
          />
          <NextButton
            slate
            ghost
            xs
            icon="i-lucide-pencil"
            :disabled="isSubmitting"
            :aria-label="t('KANBAN.ADMIN.STAGES.EDIT', { name: stage.name })"
            @click="openEdit(stage)"
          />
          <NextButton
            ruby
            ghost
            xs
            icon="i-lucide-trash-2"
            :disabled="isSubmitting"
            :aria-label="t('KANBAN.ADMIN.STAGES.REMOVE', { name: stage.name })"
            @click="attemptRemove(stage)"
          />
        </div>
      </li>
    </ul>

    <!-- Passo 2 da remoção: o servidor respondeu 409 com a contagem, e agora
         a pergunta tem destino. O aviso do REINÍCIO do tempo na etapa não é
         detalhe: é o dado que alimenta a meta de SLA. -->
    <div
      v-if="removal"
      data-testid="kanban-stage-removal"
      class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak"
    >
      <h4 class="m-0 text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.ADMIN.REMOVE_STAGE.TITLE', { stage: removal.stageName }) }}
      </h4>
      <p class="m-0 text-xs text-n-slate-11">
        {{
          t('KANBAN.ADMIN.REMOVE_STAGE.CARDS_NOTICE', {
            count: removal.cardsCount,
          })
        }}
      </p>
      <div class="flex flex-col gap-1">
        <label
          :for="`kanban-removal-target-${funnel.id}`"
          class="text-xs font-medium text-n-slate-12"
        >
          {{ t('KANBAN.ADMIN.REMOVE_STAGE.TARGET_LABEL') }}
        </label>
        <select
          :id="`kanban-removal-target-${funnel.id}`"
          v-model="removal.targetStageId"
          class="w-full px-3 py-2 text-sm border-0 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-alpha-black2 text-n-slate-12"
        >
          <option
            v-for="stage in removalTargets"
            :key="stage.id"
            :value="stage.id"
          >
            {{ stage.name }}
          </option>
        </select>
      </div>
      <p class="m-0 flex items-start gap-1 text-xs text-n-amber-12">
        <span
          aria-hidden="true"
          class="i-lucide-triangle-alert size-3 shrink-0 mt-0.5"
        />
        {{ t('KANBAN.ADMIN.REMOVE_STAGE.RESET_WARNING') }}
      </p>
      <div class="flex items-center gap-2">
        <NextButton
          ruby
          faded
          xs
          :disabled="isSubmitting || !removal.targetStageId"
          :label="t('KANBAN.ADMIN.REMOVE_STAGE.CONFIRM')"
          @click="confirmRemoval"
        />
        <NextButton
          slate
          ghost
          xs
          :label="t('KANBAN.ADMIN.REMOVE_STAGE.CANCEL')"
          @click="cancelRemoval"
        />
      </div>
    </div>

    <div
      v-if="formTarget !== null"
      data-testid="kanban-stage-form"
      class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak"
    >
      <h4 class="m-0 text-sm font-medium text-n-slate-12">
        {{
          isEditing
            ? t('KANBAN.ADMIN.STAGES.EDIT_TITLE')
            : t('KANBAN.ADMIN.STAGES.NEW_TITLE')
        }}
      </h4>
      <Input
        :id="fieldId('name')"
        ref="nameInputRef"
        v-model="form.name"
        size="sm"
        maxlength="255"
        :label="t('KANBAN.ADMIN.STAGES.NAME_LABEL')"
        :placeholder="t('KANBAN.ADMIN.STAGES.NAME_PLACEHOLDER')"
        @enter="submitForm"
      />
      <div class="flex flex-wrap gap-2">
        <Input
          :id="fieldId('wip')"
          v-model="form.wipLimit"
          type="number"
          size="sm"
          min="1"
          class="flex-1 min-w-[140px]"
          :label="t('KANBAN.ADMIN.STAGES.WIP_LIMIT_LABEL')"
          :message="t('KANBAN.ADMIN.STAGES.WIP_LIMIT_HINT')"
        />
        <Input
          :id="fieldId('sla')"
          v-model="form.slaHours"
          type="number"
          size="sm"
          min="1"
          class="flex-1 min-w-[140px]"
          :label="t('KANBAN.ADMIN.STAGES.SLA_HOURS_LABEL')"
          :message="t('KANBAN.ADMIN.STAGES.SLA_HOURS_HINT')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label
          :for="fieldId('role')"
          class="text-xs font-medium text-n-slate-12"
        >
          {{ t('KANBAN.ADMIN.STAGES.REQUIRED_ROLE_LABEL') }}
        </label>
        <select
          :id="fieldId('role')"
          v-model="form.requiredRole"
          class="w-full px-3 py-2 text-sm border-0 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-blue-9 bg-n-alpha-black2 text-n-slate-12"
        >
          <option value="">
            {{ t('KANBAN.ADMIN.STAGES.REQUIRED_ROLE_ANY') }}
          </option>
          <option v-for="role in ROLE_OPTIONS" :key="role" :value="role">
            {{ roleLabel(role) }}
          </option>
        </select>
      </div>
      <div class="flex items-center gap-2">
        <NextButton
          blue
          faded
          xs
          class="!text-n-blue-12"
          :disabled="!canSubmitForm"
          :label="t('KANBAN.ADMIN.STAGES.SAVE')"
          @click="submitForm"
        />
        <NextButton
          slate
          ghost
          xs
          :label="t('KANBAN.ADMIN.STAGES.CANCEL')"
          @click="closeForm"
        />
      </div>
    </div>

    <p v-if="errorKey" role="alert" class="m-0 text-xs text-n-ruby-11">
      {{
        t(`KANBAN.ADMIN.ERRORS.${errorKey}`, { count: errorCardsCount ?? 0 })
      }}
    </p>
  </div>
</template>
